import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAirline } from '../contexts/AirlineContext';
import { supabase } from '../lib/supabase';
import { User, Mail, Shield, Building, Check, AlertCircle, UserCog } from 'lucide-react';

type InstructorTypeCode =
  | 'FI'
  | 'CFI-I'
  | 'MEI'
  | 'UPRT'
  | 'SFI'
  | 'TRI'
  | 'Examiner'
  | 'TKI';

const INSTRUCTOR_TYPE_OPTIONS: Array<{ code: InstructorTypeCode; label: string }> = [
  { code: 'FI', label: 'FI (Basic Flight Instructor)' },
  { code: 'CFI-I', label: 'CFI-I (Instrument)' },
  { code: 'MEI', label: 'MEI (Multi-Engine)' },
  { code: 'UPRT', label: 'UPRT (Upset Prevention)' },
  { code: 'SFI', label: 'SFI (Synthetic Flight Instructor)' },
  { code: 'TRI', label: 'TRI (Type Rating Instructor)' },
  { code: 'Examiner', label: 'Examiner (skill test / LPC / OPC)' },
  { code: 'TKI', label: 'TKI (Theoretical Knowledge)' },
];

export default function Settings() {
  const { user } = useAuth();
  const { visibleAirlines, refreshAirlines } = useAirline();

  const [currentDefaultAirlineId, setCurrentDefaultAirlineId] = useState<string>('');
  const [selectedAirlineId, setSelectedAirlineId] = useState<string>('');
  const [currentInstructorType, setCurrentInstructorType] =
    useState<InstructorTypeCode | ''>('');
  const [selectedInstructorType, setSelectedInstructorType] =
    useState<InstructorTypeCode | ''>('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const loadProfileDefaults = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from('profiles')
      .select('default_airline_id, default_instructor_type')
      .eq('user_id', user.id)
      .single();
    if (error) {
      console.warn('[Settings] Failed to load profile defaults:', error.message);
      return;
    }
    const defaultAirlineId = data?.default_airline_id ?? '';
    const instructorType = (data?.default_instructor_type as InstructorTypeCode | null) ?? '';
    setCurrentDefaultAirlineId(defaultAirlineId);
    setSelectedAirlineId(defaultAirlineId);
    setCurrentInstructorType(instructorType);
    setSelectedInstructorType(instructorType);
  }, [user?.id]);

  // Load defaults from DB on mount / when user changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadProfileDefaults();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadProfileDefaults]);

  // Auto-dismiss toast with cleanup to avoid updates after unmount.
  useEffect(() => {
    if (!toast) return;
    const timeoutId = window.setTimeout(() => {
      setToast(null);
    }, 3000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [toast]);

  const airlineChanged =
    selectedAirlineId !== currentDefaultAirlineId;
  const instructorTypeChanged = selectedInstructorType !== currentInstructorType;
  const hasChanged = airlineChanged || instructorTypeChanged;

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setToast(null);

    const { error } = await supabase
      .from('profiles')
      .update({
        default_airline_id: selectedAirlineId || null,
        default_instructor_type: selectedInstructorType || null,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id);

    if (error) {
      setToast({ type: 'error', message: 'Failed to save settings. Please try again.' });
    } else {
      if (airlineChanged) await refreshAirlines();
      await loadProfileDefaults();
      setCurrentInstructorType(selectedInstructorType);
      setToast({ type: 'success', message: 'Settings saved.' });
    }

    setSaving(false);
  };

  const userName = user?.user_metadata?.full_name || user?.email || 'User';
  const userEmail = user?.email || 'Unknown';
  const userRole = user?.user_metadata?.role || 'Unknown';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-heading font-bold text-navy">Settings</h1>

      {/* Profile Information */}
      <div className="card border border-gray-200">
        <h2 className="text-lg font-semibold text-navy mb-4">Profile Information</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <User size={18} className="text-steel flex-shrink-0" />
            <div>
              <p className="text-xs text-steel font-medium">Full Name</p>
              <p className="text-sm text-navy font-semibold">{userName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail size={18} className="text-steel flex-shrink-0" />
            <div>
              <p className="text-xs text-steel font-medium">Email</p>
              <p className="text-sm text-navy font-semibold">{userEmail}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-steel flex-shrink-0" />
            <div>
              <p className="text-xs text-steel font-medium">Role</p>
              <p className="text-sm text-navy font-semibold capitalize">{userRole}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Default Airline */}
      <div className="card border border-gray-200">
        <h2 className="text-lg font-semibold text-navy mb-1">Default Airline</h2>
        <p className="text-sm text-steel mb-4">
          Chosen automatically when you log in.
        </p>
        <div className="flex items-center gap-3">
          <Building size={18} className="text-steel flex-shrink-0" />
          <select
            value={selectedAirlineId}
            onChange={(e) => setSelectedAirlineId(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy"
          >
            <option value="">No default</option>
            {visibleAirlines.map((airline) => (
              <option key={airline.airline_id} value={airline.airline_id}>
                {airline.name} ({airline.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Instructor Settings */}
      <div className="card border border-gray-200">
        <h2 className="text-lg font-semibold text-navy mb-1">Instructor Settings</h2>
        <p className="text-sm text-steel mb-4">
          Prefills new session forms so the tier-calibrated instructor assessment
          gets the right context. Leave blank to let the pipeline derive from session
          data (falls back to "Building Competencies").
        </p>
        <div className="flex items-center gap-3">
          <UserCog size={18} className="text-steel flex-shrink-0" />
          <select
            value={selectedInstructorType}
            onChange={(e) => setSelectedInstructorType(e.target.value as InstructorTypeCode | '')}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy"
          >
            <option value="">— Auto-derive —</option>
            {INSTRUCTOR_TYPE_OPTIONS.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Save row — single source of truth for both fields */}
      {toast && (
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-800'
              : 'bg-red-50 text-red-800'
          }`}
        >
          {toast.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanged || saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
